import type { Unsubscribe } from 'firebase/firestore'
import { driverSupportFirestoreService, type SupportMessage, type SupportTicket } from './driverSupportFirestoreService'

const STORAGE_PREFIX = 'valt-ds-last-read:'
const OPEN_TICKET_KEY = 'valt-ds-open-ticket'
const MAX_WATCHERS = 40

/** Desk = support agent. Manage = team manager. Same person can use both; reads must not clear the other. */
export type SupportUnreadSurface = 'desk' | 'manage'

function consoleIdentity(): { id: string; email: string } {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}') as Record<string, unknown>
    return {
      id: u.id != null ? String(u.id) : '',
      email: String(u.email || '')
        .trim()
        .toLowerCase(),
    }
  } catch {
    return { id: '', email: '' }
  }
}

function identityPart(staffUid: string) {
  const me = consoleIdentity()
  return `${me.id || '-'}|${me.email || '-'}|${staffUid || '-'}`
}

function storageKey(staffUid: string, surface: SupportUnreadSurface) {
  return `${STORAGE_PREFIX}${surface}:${identityPart(staffUid)}`
}

function legacyKey(staffUid: string) {
  return `${STORAGE_PREFIX}${staffUid}`
}

function parseMap(raw: string | null): Record<string, number> {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Record<string, number>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function loadMap(staffUid: string, surface: SupportUnreadSurface): Record<string, number> {
  if (!staffUid || typeof localStorage === 'undefined') return {}
  const own = parseMap(localStorage.getItem(storageKey(staffUid, surface)))
  if (Object.keys(own).length > 0) return own
  return parseMap(localStorage.getItem(legacyKey(staffUid)))
}

function saveMap(staffUid: string, surface: SupportUnreadSurface, map: Record<string, number>) {
  if (!staffUid || typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(storageKey(staffUid, surface), JSON.stringify(map))
  } catch {
    /* quota / private mode */
  }
}

export function formatUnreadBadge(n: number) {
  if (n <= 0) return ''
  return n > 99 ? '99+' : String(n)
}

/** Tab badges: one unread conversation, not one per driver message. */
export function unreadConversationCount(tickets: SupportTicket[], counts: Record<string, number>) {
  return tickets.reduce((n, t) => n + ((counts[t.id] || 0) > 0 ? 1 : 0), 0)
}

export function countDriverUnread(messages: SupportMessage[], lastReadMs: number) {
  return messages.filter((m) => {
    if (m.senderType !== 'driver') return false
    const t = m.createdAt?.getTime()
    if (t == null) return true
    return t > lastReadMs
  }).length
}

export function setOpenSupportTicket(ticketId: string | null) {
  if (typeof sessionStorage === 'undefined') return
  try {
    if (ticketId) sessionStorage.setItem(OPEN_TICKET_KEY, ticketId)
    else sessionStorage.removeItem(OPEN_TICKET_KEY)
  } catch {
    /* ignore */
  }
}

function openTicketIdFromSession() {
  if (typeof sessionStorage === 'undefined') return null
  try {
    return sessionStorage.getItem(OPEN_TICKET_KEY)
  } catch {
    return null
  }
}

export function markSupportTicketRead(
  staffUid: string,
  ticketId: string,
  messages?: SupportMessage[],
  surface: SupportUnreadSurface = 'desk',
) {
  if (!staffUid || !ticketId) return
  const map = loadMap(staffUid, surface)
  let at = Date.now()
  if (messages && messages.length) {
    const last = messages.reduce((max, m) => Math.max(max, m.createdAt?.getTime() || 0), 0)
    if (last > 0) at = last
  }
  map[ticketId] = at
  saveMap(staffUid, surface, map)
}

function seedUnseen(staffUid: string, surface: SupportUnreadSurface, tickets: SupportTicket[]) {
  const map = loadMap(staffUid, surface)
  let changed = false
  for (const t of tickets) {
    if (map[t.id] == null) {
      map[t.id] = t.updatedAt?.getTime() ?? Date.now()
      changed = true
    }
  }
  if (changed) saveMap(staffUid, surface, map)
  return map
}

/** Live unread counts for driver messages after this surface last opened the thread. */
export function createUnreadTracker(onCounts: (counts: Record<string, number>) => void) {
  const listeners = new Map<string, Unsubscribe>()
  const counts: Record<string, number> = {}
  let staffUid = ''
  let surface: SupportUnreadSurface = 'desk'
  let openTicketId: string | null = null

  const emit = () => onCounts({ ...counts })

  const stopAll = () => {
    for (const stop of listeners.values()) stop()
    listeners.clear()
    for (const k of Object.keys(counts)) delete counts[k]
  }

  const sync = (tickets: SupportTicket[]) => {
    if (!staffUid) {
      stopAll()
      emit()
      return
    }
    const viewing = openTicketId || openTicketIdFromSession()
    const map = seedUnseen(staffUid, surface, tickets)
    const want: string[] = []
    for (const t of tickets) {
      if (t.status === 'closed') continue
      if (viewing && t.id === viewing) continue
      const last = map[t.id] ?? 0
      const updated = t.updatedAt?.getTime() ?? 0
      if (updated > last || (counts[t.id] || 0) > 0) want.push(t.id)
      if (want.length >= MAX_WATCHERS) break
    }
    const keep = new Set(want)
    for (const [id, stop] of listeners) {
      if (!keep.has(id)) {
        stop()
        listeners.delete(id)
        delete counts[id]
      }
    }
    for (const id of want) {
      if (listeners.has(id)) continue
      listeners.set(
        id,
        driverSupportFirestoreService.watchMessages(id, (msgs) => {
          const last = loadMap(staffUid, surface)[id] ?? 0
          const viewing = openTicketId === id || openTicketIdFromSession() === id
          counts[id] = viewing ? 0 : countDriverUnread(msgs, last)
          emit()
        }),
      )
    }
    emit()
  }

  return {
    update(
      nextStaffUid: string,
      tickets: SupportTicket[],
      viewingTicketId: string | null = null,
      nextSurface: SupportUnreadSurface = 'desk',
    ) {
      staffUid = nextStaffUid
      surface = nextSurface
      openTicketId = viewingTicketId
      if (viewingTicketId) {
        counts[viewingTicketId] = 0
      }
      sync(tickets)
    },
    stop() {
      stopAll()
    },
  }
}
